import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Flower, MagnifyingGlass, Trash, Users } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
        setUsers(response.data);
        setFilteredUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user => 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.user_id.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      setDeleting(true);
      await axios.delete(`${API}/admin/users/${userToDelete.user_id}`, { withCredentials: true });
      
      setUsers(users.filter(u => u.user_id !== userToDelete.user_id));
      toast.success(`User ${userToDelete.name} removed successfully`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white/70 backdrop-blur-lg border-b border-white/20 sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button 
              data-testid="back-button"
              onClick={() => navigate('/admin/flower-exchange')}
              variant="ghost"
              size="sm"
            >
              ← Back
            </Button>
            <Flower size={28} weight="fill" className="text-primary" />
            <span className="text-xl font-heading font-bold">User Management</span>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-4xl font-heading font-bold mb-2">Registered Users</h1>
                <p className="text-muted-foreground">
                  Total users: <span className="font-bold text-primary">{filteredUsers.length}</span>
                  {searchTerm && ` (filtered from ${users.length})`}
                </p>
              </div>
            </div>

            <div className="relative">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search by name, email, or user ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 rounded-xl h-12"
              />
            </div>
          </div>

          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 bg-white/80 backdrop-blur-md border border-white/20 rounded-3xl">
              <Users size={64} weight="fill" className="text-muted mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">
                {searchTerm ? 'No matching users' : 'No users yet'}
              </h3>
              <p className="text-muted-foreground">
                {searchTerm ? 'Try adjusting your search terms' : 'Users will appear here as they join'}
              </p>
            </div>
          ) : (
            <div className="bg-white/80 backdrop-blur-md border border-white/20 shadow-lg rounded-3xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">#</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Profile</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">User ID</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Joined</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Social Links</th>
                      <th className="px-6 py-4 text-left text-sm font-heading font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredUsers.map((user, idx) => (
                      <motion.tr
                        key={user.user_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.05 }}
                        className="hover:bg-muted/30 transition-colors"
                        data-testid={`user-row-${idx}`}
                      >
                        <td className="px-6 py-4 text-sm">{idx + 1}</td>
                        <td className="px-6 py-4">
                          <img 
                            src={user.picture} 
                            alt={user.name}
                            className="w-10 h-10 rounded-full border-2 border-primary/20"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium">{user.name}</div>
                        </td>
                        <td className="px-6 py-4 text-sm">{user.email}</td>
                        <td className="px-6 py-4 text-xs text-muted-foreground font-mono">{user.user_id}</td>
                        <td className="px-6 py-4 text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="space-y-1">
                            {user.instagram && <div className="text-xs">IG: {user.instagram}</div>}
                            {user.facebook && <div className="text-xs">FB: {user.facebook}</div>}
                            {user.twitter && <div className="text-xs">X: {user.twitter}</div>}
                            {!user.instagram && !user.facebook && !user.twitter && (
                              <span className="text-muted-foreground">None</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Button
                            data-testid={`delete-user-button-${idx}`}
                            onClick={() => handleDeleteClick(user)}
                            variant="destructive"
                            size="sm"
                            className="rounded-full"
                          >
                            <Trash size={16} className="mr-1" />
                            Remove
                          </Button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-8 bg-gradient-to-br from-[#FF9A9E] to-[#FECFEF] rounded-2xl p-6 text-white">
            <h3 className="font-heading font-semibold mb-2">⚠️ Important</h3>
            <ul className="text-white/90 text-sm space-y-1">
              <li>• Removing a user will permanently delete their account and all associated data</li>
              <li>• This includes: posts, comments, messages, likes, and flower exchange registration</li>
              <li>• This action cannot be undone</li>
            </ul>
          </div>
        </motion.div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{userToDelete?.name}</strong> ({userToDelete?.email}) and all their data including:
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li>Posts and comments</li>
                <li>Messages and likes</li>
                <li>Flower exchange registration</li>
                <li>All sessions</li>
              </ul>
              <p className="mt-2 font-semibold text-destructive">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Yes, Delete User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
